import { create } from 'zustand';
import { idb, ensureDeviceId } from '../lib/idb';
import { api } from '../lib/api';
import type { User } from '@nudge/shared';

interface AuthState {
  ready: boolean;
  user: User | null;
  token: string | null;
  deviceId: string | null;
  init: () => Promise<void>;
  register: (displayName: string) => Promise<User>;
  setUser: (u: User) => void;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ready: false,
  user: null,
  token: null,
  deviceId: null,

  async init() {
    const deviceId = await ensureDeviceId();
    const token = await idb.getToken();
    set({ deviceId, token });
    if (token) {
      try {
        const { user } = await api.me();
        set({ user });
      } catch {
        // try refresh
        try {
          const { token: fresh, user } = await api.refresh(deviceId);
          await idb.setToken(fresh);
          set({ token: fresh, user });
        } catch {
          await idb.setToken(null);
          set({ token: null, user: null });
        }
      }
    }
    set({ ready: true });
  },

  async register(displayName: string) {
    let deviceId = get().deviceId;
    if (!deviceId) deviceId = await ensureDeviceId();
    const { token, user } = await api.register(deviceId, displayName);
    await idb.setToken(token);
    set({ token, user, deviceId });
    return user;
  },

  setUser(u) { set({ user: u }); },

  async logout() {
    await idb.clear();
    set({ user: null, token: null, deviceId: null });
  },
}));
